import React, { useState } from 'react';
import {
  Box,
  Button,
  Stack,
  Typography,
  Alert,
  AlertTitle,
  CircularProgress,
  TextField,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import CampaignIcon from '@mui/icons-material/Campaign';
import api from '../../services/api';

// One-click reassurance ("disregard") email for the current festival's
// approved & paid vendors. The recipient set is resolved server-side and is
// always scoped to a single festival, so past-year vendors are never included.
const DisregardEmailButton = ({
  variant = 'outlined',
  size = 'small',
  color = 'secondary',
  sx,
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const redirect = preview?.emailRedirect || result?.emailRedirect || null;
  const redirectActive = Boolean(redirect?.enabled && redirect?.redirectTo);

  const handleOpen = async () => {
    setOpen(true);
    setResult(null);
    setError(null);
    setLoading(true);
    try {
      const res = await api.get('/admin/disregard-email/recipients');
      setPreview(res.data);
      setSubject(res.data.defaultSubject || '');
      setMessage(res.data.defaultMessage || '');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load recipients');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    setSending(true);
    setError(null);
    setResult(null);
    try {
      const res = await api.post('/admin/disregard-email/send', { subject, message });
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send disregard email');
    } finally {
      setSending(false);
    }
  };

  const festivalLabel =
    preview?.festival?.name || preview?.festival?.year || 'the current festival';

  return (
    <>
      <Tooltip title="Reassure this festival's approved & paid vendors after a mistaken booth-release email">
        <span>
          <Button
            variant={variant}
            size={size}
            color={color}
            startIcon={<CampaignIcon />}
            onClick={handleOpen}
            sx={{ textTransform: 'none', ...sx }}
          >
            Send disregard email
          </Button>
        </span>
      </Tooltip>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send disregard email</DialogTitle>
        <DialogContent dividers>
          {loading ? (
            <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Stack spacing={2}>
              {error && <Alert severity="error">{error}</Alert>}

              {redirectActive && (
                <Alert severity="warning">
                  <AlertTitle>Email redirect is active</AlertTitle>
                  This email will <strong>not</strong> be sent to the real vendors. Every message
                  will be redirected to <strong>{redirect.redirectTo}</strong> (staging safety
                  net).
                </Alert>
              )}

              {result ? (
                <Stack spacing={1}>
                  <Alert severity={result.failed > 0 ? 'warning' : 'success'}>
                    Sent to {result.sent} of {result.recipientCount} vendor
                    {result.recipientCount === 1 ? '' : 's'}
                    {result.failed > 0 ? ` (${result.failed} could not be delivered)` : ''}.
                  </Alert>
                  {result.failed > 0 && Array.isArray(result.results) && (
                    <Box
                      sx={{
                        maxHeight: 180,
                        overflowY: 'auto',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 1,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Not delivered (suppressed/invalid in Postmark — contact these vendors another way)
                      </Typography>
                      <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                        {result.results
                          .filter((r) => !r.success)
                          .map((r) => (
                            <Typography key={r.email} variant="caption">
                              <strong>{r.email}</strong>
                              {r.error ? ` — ${r.error}` : ''}
                            </Typography>
                          ))}
                      </Stack>
                    </Box>
                  )}
                </Stack>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary">
                    This goes only to <strong>approved &amp; paid</strong> vendors for{' '}
                    <strong>{festivalLabel}</strong>. Past-year vendors are never included.
                  </Typography>
                  <Alert severity="info">
                    {preview
                      ? `${preview.recipientCount} vendor${preview.recipientCount === 1 ? '' : 's'} will receive this email.`
                      : 'Loading recipients…'}
                    {preview?.alreadySentCount > 0
                      ? ` ${preview.alreadySentCount} already received it recently and will be skipped.`
                      : ''}
                  </Alert>
                  <TextField
                    label="Subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    fullWidth
                    multiline
                    minRows={10}
                    helperText={
                      'Use {{booths}} for the vendor\u2019s confirmed booth(s) and {{business}} for their business name. '
                      + 'If {{booths}} is omitted, their booth list is added automatically.'
                    }
                  />

                  {Array.isArray(preview?.recipients) && preview.recipients.length > 0 && (
                    <Box
                      sx={{
                        maxHeight: 160,
                        overflowY: 'auto',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 1,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Recipients &amp; their confirmed booths
                      </Typography>
                      <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                        {preview.recipients.map((r) => (
                          <Typography key={r.email} variant="caption">
                            <strong>{r.businessName || r.email}</strong>
                            {Array.isArray(r.booths) && r.booths.length
                              ? ` — ${r.booths.join(', ')}`
                              : ''}
                          </Typography>
                        ))}
                      </Stack>
                    </Box>
                  )}
                </>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{result ? 'Close' : 'Cancel'}</Button>
          {!result && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<CampaignIcon />}
              onClick={handleSend}
              disabled={
                loading
                || sending
                || !preview?.recipientCount
                || !subject.trim()
                || !message.trim()
              }
            >
              {sending ? 'Sending…' : `Send to ${preview?.recipientCount ?? 0}`}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DisregardEmailButton;
